import { useEffect, useState } from "react";
import { Modal, message } from "antd";
import { MenuSource, MenuSourceTile } from "./MenuSourceTile";
import { AssignSourceModal, DesignOption } from "./AssignSourceModal";
import YextApi, { YextMenuTile, yextErrorMessage } from "../../api/YextApi";
import { env } from "../../env/env";
import "./ListingsHomeTab.scss";

// The design thumbnail is derived from the designId at render time (the renderer serves it), so we
// don't persist the URL — mirrors the non-upload branch of AssignSourceModal's designThumbUrl.
function menuThumbUrl(designId: string): string {
  return `${env.rendererPath}/design/thumbnail/${designId}?height=${env.rendererImageHeight}`;
}

function toMenuSource(t: YextMenuTile): MenuSource {
  return { id: String(t.id), designId: t.designId, name: t.name, thumbUrl: menuThumbUrl(t.designId), status: t.status };
}

interface Props {
  locationId?: number;
}

export function ListingsHomeTab({ locationId }: Props) {
  // Starts empty — the user assigns menus from the "No menu assigned" tile.
  const [menus, setMenus] = useState<MenuSource[]>([]);
  const [assignOpen, setAssignOpen] = useState(false);
  // The tile being re-assigned, or null when assigning a brand-new menu from the empty tile.
  const [editingId, setEditingId] = useState<string | null>(null);
  // Id of the menu currently being pushed to Yext (drives the Go Live spinner).
  const [publishingId, setPublishingId] = useState<string | null>(null);

  // Load the persisted menu tiles for this location so they survive a refresh.
  useEffect(() => {
    if (!locationId) return;
    YextApi.getMenus(locationId).then(({ data }) => setMenus(data.map(toMenuSource)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  const openAssign = (id: string | null) => {
    setEditingId(id);
    setAssignOpen(true);
  };

  const closeAssign = () => {
    setAssignOpen(false);
    setEditingId(null);
  };

  const handleSetSource = async (design: DesignOption) => {
    if (!locationId) {
      message.error("No location selected.");
      closeAssign();
      return;
    }
    const req = { designId: design.id, name: design.name, status: "not_live" };
    try {
      if (editingId === null) {
        // Assigning a new menu from the empty tile.
        const { data } = await YextApi.createMenu(locationId, req);
        setMenus((prev) => [...prev, toMenuSource(data)]);
      } else {
        // Re-assigning the source of an existing menu tile (resets it to not-live).
        const { data } = await YextApi.updateMenu(locationId, Number(editingId), req);
        setMenus((prev) => prev.map((m) => (m.id === editingId ? toMenuSource(data) : m)));
      }
    } catch (e) {
      message.error(yextErrorMessage(e, "Failed to save menu."));
    } finally {
      closeAssign();
    }
  };

  const setLocalStatus = (id: string, status: MenuSource["status"]) =>
    setMenus((prev) => prev.map((m) => (m.id === id ? { ...m, status } : m)));

  // Persist a status change (pause/resume) for a tile.
  const changeStatus = async (menu: MenuSource, status: MenuSource["status"]) => {
    if (!locationId) return;
    try {
      await YextApi.updateMenu(locationId, Number(menu.id), {
        designId: menu.designId,
        name: menu.name,
        status,
      });
      setLocalStatus(menu.id, status);
    } catch (e) {
      message.error(yextErrorMessage(e, "Failed to update menu."));
    }
  };

  // "Go Live" builds the menu from the assigned design, pushes it to Yext, and persists the live status.
  const publishMenu = async (menu: MenuSource) => {
    if (!locationId) {
      message.error("No location selected.");
      return;
    }
    if (publishingId) return; // already publishing — ignore double clicks
    setPublishingId(menu.id);
    try {
      // The backend pushes the menu, marks this tile live, and associates all live menus with the entity.
      await YextApi.pushMenuFromDesign(locationId, menu.designId);
      setLocalStatus(menu.id, "live");
      message.success("Menu published to your listing.");
    } catch (e) {
      message.error(yextErrorMessage(e, "Failed to publish menu."));
    } finally {
      setPublishingId(null);
    }
  };

  const removeMenu = (menu: MenuSource) => {
    if (!locationId) return;
    Modal.confirm({
      title: "Delete this menu?",
      content: `"${menu.name}" will be removed from your listing. This can't be undone.`,
      okText: "Delete menu",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await YextApi.deleteMenu(locationId, Number(menu.id));
          setMenus((prev) => prev.filter((m) => m.id !== menu.id));
        } catch (e) {
          message.error(yextErrorMessage(e, "Failed to remove menu."));
          return Promise.reject();
        }
      },
    });
  };

  return (
    <div className="listings-home">
      <div className="menus-list">
        {menus.map((menu) => (
          <MenuSourceTile
            key={menu.id}
            menu={menu}
            publishing={publishingId === menu.id}
            // Clicking the thumbnail opens the design in the mkdesigner editor (like Menu Center), in a new tab.
            onOpenDesign={() => {
              window.open(`${env.timberContextPath}/design?id=${menu.designId}`, "_blank", "noopener,noreferrer");
            }}
            onEdit={() => openAssign(menu.id)}
            onDelete={() => removeMenu(menu)}
            // Toggle on → publish/connect; toggle off → pause syncing.
            onToggleConnect={(connected) => (connected ? publishMenu(menu) : changeStatus(menu, "paused"))}
          />
        ))}

        <button type="button" className="menus-add-btn" onClick={() => openAssign(null)}>
          + Add menu
        </button>
      </div>

      <AssignSourceModal
        open={assignOpen}
        locationId={locationId}
        onClose={closeAssign}
        onSetSource={handleSetSource}
      />
    </div>
  );
}
